/**
 * OffersInbox — employer/admin approval queue. Lists offers in status
 * `awaiting_approval` with each scenario summarized. Clicking opens the full
 * offer page where they approve / reject.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Inbox, ArrowUpRight, Clock } from "lucide-react";

const fmt = (n) => {
    const v = Number(n || 0);
    if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
    if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
    return `₹${v.toLocaleString("en-IN")}`;
};

export default function OffersInbox() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/offers/inbox");
            setItems(data.items || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    return (
        <div className="max-w-[1200px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-4" data-testid="offers-inbox">
            <div>
                <div className="text-[10px] uppercase tracking-widest font-mono text-ikb">Approvals</div>
                <h1 className="font-display font-black text-2xl sm:text-3xl flex items-center gap-2"><Inbox className="w-6 h-6" /> Offers awaiting your approval</h1>
                <p className="text-xs text-muted-foreground mt-1">Recruiters have sent these offer bundles for your review. Open one to approve or reject with a note.</p>
            </div>

            {loading && <div className="text-center text-xs font-mono text-muted-foreground py-10">Loading…</div>}
            {!loading && items.length === 0 && (
                <div className="border border-dashed border-border bg-white p-10 text-center text-sm text-muted-foreground" data-testid="offers-inbox-empty">
                    Inbox zero — no offers waiting on you.
                </div>
            )}

            <div className="space-y-2">
                {items.map((o) => {
                    const ageMs = Date.now() - new Date(o.updated_at).getTime();
                    const days = Math.floor(ageMs / 86400000);
                    const hours = Math.floor((ageMs % 86400000) / 3600000);
                    const age = days > 0 ? `${days}d ${hours}h` : `${hours}h`;
                    return (
                        <Link key={o.id} to={`/candidates/${o.candidate_id}/offers`} className="block border border-border bg-white p-4 hover:border-ikb hover:shadow-sm transition" data-testid={`offers-inbox-row-${o.id}`}>
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-display font-bold text-base">{o.candidate_name}</span>
                                        <span className="text-xs text-foreground/60">·</span>
                                        <span className="text-xs">{o.title}</span>
                                        {o.job_title && <><span className="text-xs text-foreground/60">·</span><span className="text-xs text-foreground/70">{o.job_title}</span></>}
                                    </div>
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 mt-1.5">
                                        From {o.created_by_name || "Recruiter"} · {(o.scenarios || []).length} scenarios · Current CTC {fmt(o.candidate_current_ctc)}
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                    <span className="text-[10px] font-mono uppercase tracking-widest text-amber-700 inline-flex items-center gap-1">
                                        <Clock className="w-3 h-3" /> Waiting {age}
                                    </span>
                                    <ArrowUpRight className="w-4 h-4 text-foreground/40" />
                                </div>
                            </div>
                            {/* Scenario summary chips */}
                            <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                                {(o.scenarios || []).map((s) => {
                                    const colorCls = { blue: "border-ikb text-ikb", emerald: "border-emerald-500 text-emerald-700", rose: "border-rose-500 text-rose-700", amber: "border-amber-500 text-amber-700" }[s.color] || "border-border text-foreground/70";
                                    return (
                                        <span key={s.id} className={`text-[10px] font-mono uppercase tracking-widest border bg-white px-2 py-0.5 ${colorCls}`}>
                                            {s.name} · +{s.increment_pct}% · {fmt(s.totals?.employer_ctc_annual)}
                                        </span>
                                    );
                                })}
                            </div>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}
